import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { DashboardPinIcon } from '../assets/dashboard/DashboardPinIcon';
import { ReactComponent as FlipIcon } from '../assets/tools/icon-flip_projec-active.svg';
import { ReactComponent as ArvIcon } from '../assets/tools/icon-arv_analysis_active.svg';
import { ReactComponent as ListedSaleIcon } from '../assets/dashboard/listed-for-sale.svg';
import { ReactComponent as ListingRemovedIcon } from '../assets/dashboard/listing-removed.svg';
import { ReactComponent as SoldIcon } from '../assets/dashboard/sold.svg';
import { ReactComponent as ListedRentIcon } from '../assets/dashboard/listed-for-rent.svg';
import { ReactComponent as IncreaseIcon } from '../assets/dashboard/price-increased.svg';
import { ReactComponent as DecreaseIcon } from '../assets/dashboard/price-decreased.svg';
import AppNavbar from '../features/AppNavbar';
import PageShell from '../layouts/PageShell';
import { reilmAPI } from '../services/apiClient';
import { STAGE_OPTIONS, STRATEGY_OPTIONS } from '../features/pipeline/options';
import { groupHeadingStyle, placeholderStyle } from '../features/pipeline/selectStyles';
import { getStatusLabel, getStrategyLabel, resolveSelectOption } from '../features/pipeline/utils';
import { getIn } from './getIn';
import { parseCalendarDate } from '../features/arv/calendarDate';

const PROPERTY_TYPE_LABELS = {
  single_family: 'Single Family',
  multi_family: 'Multi Family',
  condo: 'Condo',
  townhouse: 'Townhouse',
};

// Same bound as fieldLimits.js's yearBuiltMin, kept local rather than imported: that entry also
// carries Arv Search Criteria's message/zeroAsUnset, neither of which applies to this screen's
// plain null-on-invalid save.
const YEAR_FIELD_MIN = 1800;
const YEAR_FIELD_MAX = 2099;

// Same bounds as reilm-be's RECOMPUTE_CRITERIA_LIMITS and fieldLimits.js's beds/baths/sqft entries,
// kept local for the same reason as the year bound above. These feed calcScore/compAdjustments
// directly whenever arv.criteria's own min is empty, so an unbounded typo here (18000 instead of
// 1800) reaches the ARV estimate unchecked.
const BEDS_FIELD_MIN = 0;
const BEDS_FIELD_MAX = 15;
const BATHS_FIELD_MIN = 0;
const BATHS_FIELD_MAX = 15;
const SQFT_FIELD_MIN = 0;
const SQFT_FIELD_MAX = 10000;

const STATUS_ICON_MAP = {
  'Listed':     ListedSaleIcon,
  'Listing removed': ListingRemovedIcon,
  'Sold':            SoldIcon,
  "Listed for rent":     ListedRentIcon,
  price_increase:  IncreaseIcon,
  price_decrease:  DecreaseIcon,
};

const STATUS_LABEL_MAP = {
  listed_sale:     'Listed for Sale',
  listing_removed: 'Withdrawn',
  sold:            'Sold',
  listed_rent:     'Listed for Rent',
  price_increase:  'Price Increased',
  price_decrease:  'Price Decreased',
};

// The three maps above are keyed on entry.event_name, and only STATUS_ICON_MAP survives that. Its
// first four keys are the display strings the provider actually writes, so 312 of the 396 events in
// the live collection do render an icon. The other two keys, and every key in the two maps below,
// are internal codes the provider never writes. Census over the live collection, 30.08, 396 events
// across 37 properties: Sold 117, Listing removed 83, Listed 69, Price Changed 66, Listed for rent
// 43, Price Changed for rent 12, Relisted 6. So STATUS_LABEL_MAP is dead outright, and the price
// pair is unreachable in all three maps - defect 24, inherited from 45080a2. Re-keying is mechanical
// everywhere except that pair: the provider says only "Price Changed", so up-versus-down has to come
// from consecutive prices. pages/ListingDetails.jsx already re-keyed its own copies.
//
// STATUS_BADGE_BG is dead for a second, independent reason, and that one no re-keying fixes. Every
// one of the six assets these rows render opens with a 20x20 fill circle - rect 0.5/0.5, 19x19,
// rx 9.5 - exactly coincident with the size-[20px] rounded-full wrapper this map colours. The icon
// covers the background in every branch, on both screens. ListingDetails is the proof: it has two
// live keys and its two values match its assets' own circle colours, which is why nobody noticed
// they were doing nothing. So these six values are decoration, and this map is left as it was rather
// than "corrected" - a mutation flipping the price pair leaves all 58 tests green, because there is
// nothing to see.
//
// That matters because the design does disagree about those two circles, and the fix is an asset,
// not a value here. Measured in files/sow2-section-lean.svg, the only export on disk whose ids
// resolve: the frame named `history` - a section-level sibling of `property-dashboard` drawn inside
// its own column, x 5247-5905 against the screen's 5243-6415 - fills and strokes the badge #C6CFC6
// on five instances, while comp-details/history_2 and grid-expanded/history_3 use #CBDCC3 on four.
// price-increased.svg and price-decreased.svg carry #CBDCC3 and are imported by both this file and
// ListingDetails.jsx, so they are already right for one screen and wrong for the other, and no
// single value in them can be right for both. Defect 26; the remedy is a per-screen variant.
//
// #CBDCC3 is brand.rowBorder. In that same export it strokes ten rects, four of them under this
// screen: input_155-input_162, the four Major Systems year fields, which is where the code carried
// #DAE4D5 until 30.08.
const STATUS_BADGE_BG = {
  listed_sale:     '#D85263',
  listing_removed: null,
  sold:            null,
  listed_rent:     '#F5C312',
  price_increase:  '#CBDCC3',
  price_decrease:  '#CBDCC3',
};


const STAGE_OPTIONS_WITH_LABEL = STAGE_OPTIONS.map(g => ({
  ...g,
  options: g.options.map(o => ({ ...o, stageLabel: g.label })),
}));

// Color scale matches assets/arv/school-ratings-color.svg
// 0=gray · 1–4=mid-green · 5–7=dark-green · 8–10=brand-dark
const getSchoolRatingColor = (rate) => {
  const n = parseInt(rate, 10);
  if (!n || n <= 0) return '#C4CCBE';
  if (n <= 4)       return '#4F7A35';
  if (n <= 7)       return '#2F4A1F';
  return '#17270D';
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatHistoryDate = (iso) => {
  if (!iso) return '';
  return parseCalendarDate(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPrice = (price) => {
  if (!price && price !== 0) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
};

const selectStyles = {
  control: (base) => ({
    ...base,
    border: 'transparent',
    borderRadius: 8,
    boxShadow: 'none',
    minHeight: 40,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: 'white',
    '&:hover': { borderColor: '#4CBC18' },
  }),
  container: (base) => ({ ...base, right: '10px' }),
  // Comment Set #3, item 4: same bug as ComparableFilterRow.jsx's Show/Status options — this
  // object backs both Stage and Strategy below, so fixing it here closes both at once. Real
  // `&:hover` only, no `isSelected`/`isFocused` in the background.
  option: (base) => ({
    ...base,
    backgroundColor: '#fff',
    color: '#17270D',
    fontSize: 13,
    cursor: 'pointer',
    ':active': { backgroundColor: '#f8fef4' },
    '&:hover': { backgroundColor: '#f8fef4' },
  }),
  groupHeading: groupHeadingStyle,
  placeholder: placeholderStyle,
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: 'none' }),
  input: (base) => ({ ...base, caretColor: 'transparent' }),
};

const inputCls = 'w-full h-[40px] text-[14px] font-medium text-center border border-brand-fieldBdr rounded-[4px] px-[8px] py-[8px] bg-white outline-none focus:border-brand-green hover:border-brand-green';
// 29.06 comment, pt. 3: "field labels (not values)" -> #4F5F4A. Size 12px per spec/property-dashboard.md,
// "Підпис поля в деталях". `uppercase` dropped 26.08, second review pass: the export renders mixed
// case ("Lot Size", "Flood Zone"), measured on node property-dashboard/details/Frame 327 — the
// pre-26.08 uppercase was a guess, same category of mistake as the font-bold guess below.
const labelCls = 'block text-[12px] text-brand-secondary tracking-wide mb-[6px]';
// Same comment, pt. 3: "Section headers (Features, HOA, etc.)" -> #4F5F4A.
// `uppercase` dropped 26.08: "Schools", "Taxes" and "Major Systems" all read mixed case in the
// export, not upper.
// 14px/800, re-sampled 02.09 from sow2-section-lean.svg (09.08) — nodes details/Frame 327/Frame 595
// (Community, Schools, Taxes) and quick-actions/Frame 606/Frame 325_6/Frame 323 (Major Systems).
// The 26.08 pass read this as regular off figma-18.08/sow2-section-text.18.08.svg, which writes
// `bold` or nothing for the whole 300-900 range; its silence is not 400, and reading a weight from
// it is Дефект 27. Comment Set #1, PD 8, asks for ExtraBold here by name.
const labelSectionCls = 'block text-[14px] font-extrabold text-brand-secondary tracking-wide mb-[14px]';

// Detail-field value and its empty state, both 14px — this screen's value size, unlike
// AddOpportunity's 13px. These fields carry no onChange/save (pure display of `details.*`), so
// unlike AddOpportunity's <input placeholder="—"> (mr-04) there is no save path for the dash to
// reach in the first place; FieldValue below is the read-only shape of the same rule.
// The weights came from spec/property-dashboard.md, which read both as regular off the 18.08
// export — Дефект 27 again. Re-sampled 02.09 from sow2-section-lean.svg and corrected below.
const EM_DASH = '—';
// 14px/500, sow2-section-lean.svg node details/Frame 327/.../input_114. The empty state keeps
// regular: the dash is drawn 14/regular #8A9A85 on the same nodes, a different weight from a value
// that is actually there.
const valueCls = 'text-[14px] font-medium text-brand-dark';
const emptyValueCls = 'text-[14px] text-brand-placeholder';
const FieldValue = ({ value }) => {
  // A bare `value === ''` misses a whitespace-only string ('   ') — it is not `===` to '', so it
  // took the filled branch and rendered as data. `typeof value === 'string'` guards the trim so a
  // legitimate 0 (Lot Size, Total Tax) never goes through String coercion and is never mistaken for
  // empty.
  const isEmpty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  return <p className={isEmpty ? emptyValueCls : valueCls}>{isEmpty ? EM_DASH : value}</p>;
};

// Analysis Tools cards: SOW 3 spec home-page-rework-spec.28.09.md section 4 (D1-D12), ARV left.
const ANALYSIS_TOOLS = [
  { route: 'arv', title: 'ARV Analysis', description: 'Estimate value with comps', Icon: ArvIcon, background: '#18290E' },
  { route: 'flip', title: 'Flip Analysis', description: 'Model flips, new builds, & refinancing', Icon: FlipIcon, background: '#17270D' },
];

// Transparent border + 5px padding keeps the drawn 6px inset, so the hover border moves nothing.
// Glow offsets = drawn centre (+182, +92) minus r 26 and the border; line heights 16/18 land the
// export's baselines (+38.6, +54.6); path stroke 1 is the drawn 1.5 on a 24-box glyph shown at 36.
// A Link, not a clickable div: it navigates, so Tab and Enter reach it; the ring is the Flip focus style.
const ToolCard = ({ tool, to, state }) => {
  const { Icon, title, description, background } = tool;
  return (
    <Link
      to={to}
      state={state}
      className="block relative overflow-hidden rounded-[12px] p-[5px] border border-transparent cursor-pointer transition hover:border-brand-green hover:-translate-y-[2px] outline-none focus-visible:ring-2 focus-visible:ring-brand-green"
      style={{ background }}
    >
      <div
        className="rounded-[8px] h-[72px] px-[16px] flex items-center gap-[10px] border"
        style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}
      >
        <Icon className="[&_path]:[stroke-width:1]" style={{ width: 36, height: 36, flexShrink: 0 }} />
        <div>
          <p className="text-white font-extrabold text-[16px] leading-[16px]">{title}</p>
          <p className="text-white text-[13px] leading-[18px]">{description}</p>
        </div>
      </div>
      <div
        data-testid="tool-card-glow"
        aria-hidden="true"
        className="absolute rounded-full pointer-events-none"
        style={{ left: 155, top: 65, width: 52, height: 52, background: '#4CBC18', opacity: 0.7, filter: 'blur(45.65px)' }}
      />
    </Link>
  );
};

// Major Systems label (spec section 5, M3/M8). The tooltip panel copies FlipInfoIcon's panel style;
// that component is bound to its info glyph and cannot wrap a text label. `open` is owned by the
// row so focusing the field opens it too - a mouse-only tooltip is a gap (FlipInfoIcon.jsx).
const MajorSystemLabel = ({ htmlFor, tooltip, tooltipId, open, setOpen, children }) => (
  <span
    className="relative shrink-0"
    onMouseEnter={tooltip ? () => setOpen(true) : undefined}
    onMouseLeave={tooltip ? () => setOpen(false) : undefined}
  >
    <label htmlFor={htmlFor} className="text-[12px] text-brand-secondary">{children}</label>
    {tooltip && open ? (
      <span
        id={tooltipId}
        role="tooltip"
        className="absolute z-30 left-0 top-full mt-[2px] whitespace-nowrap rounded-[8px] bg-white p-[12px] text-[13px] leading-[20px] text-[#1A2A10] pointer-events-none"
        style={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}
      >
        {tooltip}
      </span>
    ) : null}
  </span>
);

const tabStyle = (active) => ({
  paddingBottom: 10,
  borderBottom: active ? '2px solid #4CBC18' : '2px solid transparent',
  // 29.06 comment, pt. 3: "Property Details/History when not selected" -> #4F5F4A. The underline
  // and the colour are the only difference between the two states; both carry the same weight.
  // 16/800 on details/Frame 310/Frame 337 in sow2-section-lean.svg (09.08). The 26.08 pass read
  // 400 off the 18.08 export, which cannot express a weight other than bold — Дефект 27. Comment
  // Set #1, PD 8, names both tabs.
  color: active ? '#17270D' : '#4F5F4A',
  fontSize: 16,
  fontWeight: 800,
  background: 'none',
  cursor: 'pointer',
  outline: 'none',
  whiteSpace: 'nowrap',
});

const PropertyDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  // Distinguishes "the record is gone" (404) from everything else a fetch can do -- a stubbed 500
  // used to render the same "Property not found." as a real 404, and so would a timeout or a
  // network drop. null means no failure recorded (still loading, or a 401 the interceptor is
  // already handling). 'locked' is its own third value, not folded into null: a 403 subscription
  // refusal is not "no failure recorded" (the fetch really did fail, and the record is genuinely not
  // shown), it just needs a render distinct from both of the other two -- see the `!property` branch
  // below.
  const [loadError, setLoadError] = useState(null);
  const [historyTab, setHistoryTab] = useState(false);

  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [sqft, setSqft] = useState('');
  const [stage, setStage] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [notes, setNotes] = useState('');
  const [notesModified, setNotesModified] = useState(null);
  const [roofYear, setRoofYear] = useState('');
  const [acYear, setAcYear] = useState('');
  const [whYear, setWhYear] = useState('');
  const [furnaceYear, setFurnaceYear] = useState('');
  // Which Major Systems field's tooltip is open (only WH has one); hover and focus share it.
  const [openTooltip, setOpenTooltip] = useState(null);
  const [pinned, setPinned] = useState(false);
  // Shared banner across all eight save call sites (pin, beds/baths/sqft, stage, strategy, notes,
  // Major Systems) rather than one per field -- see src/pages/README.md for the full mechanism.
  const [saveError, setSaveError] = useState(null);
  // Which call site currently owns the banner, keyed by patch shape so an unrelated field's
  // settlement cannot clear or raise this one's message. See src/pages/README.md.
  const failedSavesRef = useRef(new Set());
  // Per-key generation counter gating a stale settlement (issued earlier, resolving later) from
  // acting on a key a newer save already owns. Ported from Arv.jsx's own field of the same name;
  // the race and the id-boundary trace both live in src/pages/README.md.
  const saveGenerationsRef = useRef(new Map());
  // Best estimate (not proof) of what the server holds for a key -- read from the PATCH response
  // body (getIn, f-9), sent value as fallback, on every settled response including a stale one.
  // Read only by the pin rollback. Full reasoning in src/pages/README.md.
  const confirmedSavesRef = useRef(new Map());

  const nextSaveGeneration = (key) => {
    const gen = (saveGenerationsRef.current.get(key) ?? 0) + 1;
    saveGenerationsRef.current.set(key, gen);
    return gen;
  };

  const isCurrentSave = (key, gen) => saveGenerationsRef.current.get(key) === gen;

  // Fresh instance per id (App.js's PropertyDashboardRoute key={id}); in a normal mount this effect
  // runs once, so the removed setLoading/setLoadError/setSaveError calls here only restated each
  // state's own initializer. Losing that key reopens the confirmed-value leak and stale loading/error state; see src/pages/README.md.
  useEffect(() => {
    let cancelled = false;
    reilmAPI.getProperty(id)
      .then(({ data }) => {
        if (cancelled) return;
        const p = data.data.property;
        setProperty(p);
        setBeds(p.details?.beds ?? '');
        setBaths(p.details?.baths ?? '');
        setSqft(p.details?.sqft ?? '');
        // A stored value with no canonical match is real data, not an unset field (getStrategyLabel's
        // comment above) -- resolveSelectOption keeps this select in line with what Pipeline's grid
        // already shows for the same property via getStatusLabel/getStrategyLabel.
        setStage(resolveSelectOption(STAGE_OPTIONS_WITH_LABEL, p.stage, getStatusLabel));
        setStrategy(resolveSelectOption(STRATEGY_OPTIONS, p.strategy, getStrategyLabel));
        setNotes(p.notes?.text ?? '');
        setNotesModified(p.notes?.updatedAt ?? null);
        setPinned(p.pin ?? false);
        // Seeds the pin rollback target with the authoritative loaded value -- simpler than Arv's
        // lazy per-row seed (one row toggled among many), since pin is the one field on this screen
        // that reads confirmedSavesRef at all, and it always has a known value at load.
        confirmedSavesRef.current.set('pin', p.pin ?? false);
        setRoofYear(p.details?.roof ?? '');
        setAcYear(p.details?.ac ?? '');
        setWhYear(p.details?.water_heater ?? '');
        setFurnaceYear(p.details?.furnace ?? '');
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        // Same guard as AddOpportunity's handleFinish/onAddressChange: the interceptor in
        // services/config.js is already redirecting to #/login on a failed refresh, so setting a
        // message here would flash the wrong cause on the way out.
        if (status === 401) return;
        const code = err?.response?.data?.error?.code;
        // Same reasoning as the 401 carve-out above, for the other case config.js already handles:
        // a 403 with one of these two codes fires reilm:subscription-expired, and ExpiredModal
        // (features/subscription) renders over whatever is on screen. Neither "Property not found."
        // nor "Could not load this property, try again" fits -- the record is present and readable,
        // and a retry cannot succeed until the trial or subscription itself is fixed -- so this is
        // its own loadError value rather than an early return into the null/still-loading default:
        // the `!property` branch below renders no message for it, on purpose, rather than falling
        // through to either one. Any other 403 code (a real per-property authorization refusal)
        // still falls through to load_failed below.
        if (status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) {
          setLoadError('locked');
          return;
        }
        // Only a 404 means the record itself is gone. Everything else used to fall through to the
        // same "Property not found." render regardless of what actually happened.
        setLoadError(status === 404 ? 'not_found' : 'load_failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // save() used to be `(patch) => reilmAPI...(id, patch).catch(() => {})` -- a failed PATCH vanished
  // silently, the typed value stayed on screen, and notesModified below advanced regardless of the
  // outcome, so a failed save was indistinguishable from a successful one. Returning the outcome
  // (rather than throwing) lets a caller that needs to sequence on it -- only the notes field does --
  // do so, without every other call site needing its own catch.
  const save = (patch) => {
    // Every call site sends exactly one distinct field, so the patch's own key set is a stable
    // identity for "which control is this" without threading a name through all eight call sites.
    const key = Object.keys(patch).join(',');
    // Taken synchronously before the request goes out -- two blurs on the same key in quick
    // succession must not share a generation, or the second could never be told apart from the
    // first once both are in flight. Same shape as Arv.jsx's persistCriteriaFilters.
    const gen = nextSaveGeneration(key);
    return reilmAPI.updatedPropertiesDetails(id, patch)
      .then((res) => {
        // Unconditional, even when this settlement is stale below -- see confirmedSavesRef's own
        // comment. Reads the response body via a dotted-path getter (the response nests
        // 'details.beds' as property.details.beds, not property['details.beds']), sent value as
        // fallback if the path is absent -- README.md, "confirmed value reads the response".
        const saved = getIn(res?.data?.data?.property, key);
        confirmedSavesRef.current.set(key, saved ?? patch[key]);
        // Stale settlement resolves undefined, distinct from true/false -- full reasoning (why
        // failedSavesRef must not be touched here) in src/pages/README.md.
        if (!isCurrentSave(key, gen)) return undefined;
        failedSavesRef.current.delete(key);
        // Only clear the shared banner once nothing is left failing -- a same-field retry deletes
        // the only entry and clears it same as before; a different field resolving while another
        // field's failure is still outstanding leaves that entry in place and the banner up.
        if (failedSavesRef.current.size === 0) setSaveError(null);
        return true;
      })
      .catch((err) => {
        // Same generation gate as the success branch -- a stale failure must not raise the banner
        // over a key a newer save (which may itself still succeed) already owns.
        if (!isCurrentSave(key, gen)) return undefined;
        if (err?.response?.status === 401) return false; // interceptor already redirecting to login
        const code = err?.response?.data?.error?.code;
        // Same carve-out as the load path above: config.js already dispatches
        // reilm:subscription-expired for these two codes and ExpiredModal renders over the screen,
        // so this field is not "failing" in the sense the banner exists for and must not be added
        // to failedSavesRef -- an entry that nothing else will ever remove would keep the banner
        // stuck up after the real failure it was covering had already cleared.
        if (err?.response?.status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) {
          return false;
        }
        failedSavesRef.current.add(key);
        setSaveError("We couldn't save your changes. Please try again.");
        return false;
      });
  };

  const addressDisplay = property ? [
    property.address?.street,
    property.address?.city,
    property.address?.state,
    property.address?.zip,
  ].filter(Boolean).join(', ') : '';

  const groupedHistory = {};
  (property?.history ?? []).forEach(entry => {
    const year = entry.date ? parseCalendarDate(entry.date).getFullYear() : 'Unknown';
    if (!groupedHistory[year]) groupedHistory[year] = [];
    groupedHistory[year].push(entry);
  });
  const sortedYears = Object.keys(groupedHistory).sort((a, b) => b - a);

  if (loading) {
    return (
      <PageShell>
        <AppNavbar />
        <div className="flex items-center justify-center py-20 text-brand-muted text-[20px]">Loading…</div>
      </PageShell>
    );
  }

  if (!property) {
    // Locked renders no message at all -- ExpiredModal (mounted at App level, above this component)
    // already states the cause and carries the only useful action. Pipeline's own locked state
    // (pages/Pipeline.jsx) shows its own card repeating the same fact under the same modal; that is
    // a duplication this screen does not need to also carry. AppNavbar stays, same as Pipeline, so
    // closing the modal does not strand the user on a page with no way out.
    if (loadError === 'locked') {
      return (
        <PageShell>
          <AppNavbar />
        </PageShell>
      );
    }
    return (
      <PageShell>
        <AppNavbar />
        <div className="flex items-center justify-center py-20 text-red-400 text-[14px]">
          {loadError === 'load_failed' ? 'Could not load this property, try again' : 'Property not found.'}
        </div>
      </PageShell>
    );
  }

  const details = property.details ?? {};
  const schools = details.schools ?? {};
  const hoaArray    = details.amenities?.homeowners_association ?? []
  const hoaFeeEntry  = hoaArray.find(s => s.startsWith('Association Fee:'))
  const hoaFreqEntry = hoaArray.find(s => s.startsWith('Association Fee Frequency:'))
  const hoaFeeAmt    = hoaFeeEntry  ? Number(hoaFeeEntry.split(':')[1]?.trim())  : null
  const hoaFreq      = hoaFreqEntry ? hoaFreqEntry.split(':')[1]?.trim()         : null
  const hoaAmenities = details.amenities?.amenities_and_community_features?.[0] ?? null
  // hoaAmenities != null alone is not the gate: mapApiToForm (AddOpportunity) sets
  // details.amenities to an object for any address it resolved at all, so the object is truthy and
  // the amenities-text branch always runs; typing into that field and clearing it before save writes
  // [''] back, not an absent key or []. '' != null is true, so the section rendered on that stored
  // shape with two em dashes — exactly the "positive claim of no data" the gate above exists to
  // avoid. trim(), not a bare !== '', for the same reason FieldValue's own isEmpty trims below: a
  // whitespace-only entry is the same stored shape as '' and the same case the 26.08 measurement
  // reports as one class ("порожній або пробільний рядок"), so a bare inequality would leave this
  // gate and FieldValue disagreeing on the same string one field apart. Live measurement 26.08: 42
  // objects carry the amenities container, 5 have a real value here, 0 have an empty/whitespace
  // string today, 37 have no key — so this branch is unreached in production right now, but the
  // write path is open and it is the same category of guard as Lot Size below.
  const hasHoa       = (hoaFeeAmt != null && hoaFeeAmt > 0) || (hoaAmenities != null && String(hoaAmenities).trim() !== '')

  return (
    <PageShell>
      <AppNavbar />
      <div className=" mt-[24px] flex flex-col gap-y-[20px] pb-[60px]">

        {/* Header */}
        <div className="flex flex-col gap-y-[8px]">
          <button
            onClick={() => navigate('/pipeline')}
            className="self-start text-[13px] font-medium text-brand-green hover:opacity-70 transition-opacity"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            {/* PD 4: 20/800, line height 24. heading_6/Frame 308 in sow2-section-lean.svg. */}
            <h1 className="text-[20px] font-extrabold leading-[24px] text-brand-dark">{addressDisplay}</h1>
            {/* `group` fires the icon's own hover/focus classes (colour change on the SVG nodes);
                the discarded-return-value rollback bug and its fix are in src/pages/README.md. */}
            <button
              onClick={() => {
                const next = !pinned
                setPinned(next)
                save({ pin: next }).then((ok) => {
                  if (ok === false) setPinned(confirmedSavesRef.current.get('pin'))
                })
              }}
              className="group"
              title={pinned ? 'Unpin property' : 'Pin property'}
            >
              <DashboardPinIcon active={pinned} />
            </button>
          </div>
          {saveError && (
            <p className="text-red-400 text-[14px]">{saveError}</p>
          )}
        </div>

        {/* Figma centres a stroke on its boundary, so an exported stroked rect sits half a pixel
            inside its true box and its rx is 0.5 short of the real radius — rx=7.5 in the export
            means a real radius of 8, not a value to "correct" the surrounding rounded-[8px]
            classes down to. The `details` frame is the only one of these five that also exports a
            genuine fill rect, at integer coordinates x=5244 y=1420 width=690 height=660 rx=8,
            sitting beside its own stroke at 5244.5 1420.5 689x659 rx=7.5 — that pair is what proves
            the offset, since the other four frames emit only the stroke geometry (twice), which is
            why their "fill" reads as though it sits on a half-pixel. */}
        {/* Analysis Tools row */}
        <div className="bg-white rounded-[8px] border border-brand-fieldBdr px-[24px] pt-[8px] pb-[24px]">
          <p className="py-[16px] text-[16px] font-extrabold text-brand-dark">Analysis Tools</p>
          <div className="grid grid-cols-2 gap-[24px]">
            {ANALYSIS_TOOLS.map(tool => (
              <ToolCard
                key={tool.route}
                tool={tool}
                to={`/property/${id}/${tool.route}`}
                state={{ property }}
              />
            ))}
          </div>
        </div>

        {/* Main content row */}
        <div className="flex gap-x-[16px] items-start">

          {/* Left: Property Details card */}
          <div className="basis-[57.5%] min-h-[660px] bg-white border border-brand-fieldBdr rounded-[8px] p-[28px]">

            {/* Tab bar */}
            <div className="flex items-end gap-x-[24px] mb-[24px]">
              <button className='border-b-2 border-brand-green ' style={tabStyle(!historyTab)} onClick={() => setHistoryTab(false)}>
                Property Details
              </button>
              <button style={tabStyle(historyTab)} onClick={() => setHistoryTab(true)}>
                History
              </button>
            </div>

            {/* Property Details tab */}
            {!historyTab && (
              <div className="flex flex-col gap-y-[28px]">

                {/* Features — editable inputs */}
                <div>
                  <p className={labelSectionCls}>Features</p>
                  <div className="grid grid-cols-3 gap-x-[12px]">
                    <div>
                      <label className={labelCls} style={{display:'flex', gap:'38px'}}>
                        <span>
                          Beds
                        </span>
                        <span>
                          Baths  
                        </span>
                        <span>
                          Sq.Ft
                        </span>
                      </label>
                      <div className="flex items-center gap-x-[16px]">
                        <input
                          type="number"
                          className={inputCls}
                          value={beds}
                          onChange={e => setBeds(e.target.value)}
                          onBlur={() => {
                            // Real bound, not the old unbounded Number() -- see the Major Systems
                            // year fields below for why a huge-but-finite value needs its own check.
                            // Integer-only, same as fieldLimits.js's beds entry: a fractional value
                            // (a type="number" input with no `step` still accepts 3.5) 400s on the
                            // backend's assertRecomputeCriteriaBody rule with no field-level message,
                            // so it must never reach save() in the first place.
                            const n = Number(beds)
                            const inRange = n >= BEDS_FIELD_MIN && n <= BEDS_FIELD_MAX
                            const valid = beds !== '' && inRange && Number.isInteger(n)
                            if (!valid) setBeds('')
                            save({ 'details.beds': valid ? n : null })
                          }}
                          placeholder="Bd"
                        />
                        <input
                          type="number"
                          className={inputCls}
                          value={baths}
                          onChange={e => setBaths(e.target.value)}
                          onBlur={() => {
                            const n = Number(baths)
                            const inRange = n >= BATHS_FIELD_MIN && n <= BATHS_FIELD_MAX
                            const valid = baths !== '' && inRange
                            if (!valid) setBaths('')
                            save({ 'details.baths': valid ? n : null })
                          }}
                          placeholder="Ba"
                        />
                        <input
                          type="number"
                          className={inputCls}
                          value={sqft}
                          onChange={e => setSqft(e.target.value)}
                          onBlur={() => {
                            const n = Number(sqft)
                            const inRange = n >= SQFT_FIELD_MIN && n <= SQFT_FIELD_MAX
                            const valid = sqft !== '' && inRange
                            if (!valid) setSqft('')
                            save({ 'details.sqft': valid ? n : null })
                          }}
                          placeholder="Sqft"
                        />
                      </div>
                    </div>
                    {/* Col 2 — Stage: Status */}
                    <div>
                      <label className={labelCls}>Stage</label>
                      <Select
                        value={stage}
                        options={STAGE_OPTIONS_WITH_LABEL}
                        onChange={(opt) => { setStage(opt); save({ stage: opt?.value ?? null }); }}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        isSearchable={false}
                        placeholder="Select"
                      />
                    </div>
                    {/* Col 3 — Strategy */}
                    <div>
                      <label className={labelCls}>Strategy</label>
                      <Select
                        value={strategy}
                        options={STRATEGY_OPTIONS}
                        onChange={(opt) => { setStrategy(opt); save({ strategy: opt?.value ?? null }); }}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        isSearchable={false}
                        placeholder="Select"
                      />
                    </div>
                  </div>
                </div>

                {/* Features — 3-col read-only grid */}
                <div className="grid grid-cols-3 gap-x-[24px] gap-y-[20px]">
                  <div>
                    <span className={labelCls}>Property Type</span>
                    <FieldValue value={PROPERTY_TYPE_LABELS[property.propertyType] ?? property.propertyType} />
                  </div>
                  <div>
                    <span className={labelCls}>Year Built</span>
                    <FieldValue value={details.yearBuilt} />
                  </div>
                  <div>
                    <span className={labelCls}>Lot Size</span>
                    {/* Not a bare != null, and not the same guard as Total Tax below even though the
                        reasoning starts the same place: a lot size of 0 is a real value FieldValue
                        should render, not an absence, so the guard cannot be a truthy check either.
                        Total Tax reads through formatPrice, which already turns '' into '' via its
                        own `!price && price !== 0` check before FieldValue ever sees it (see
                        formatPrice, top of file) — this field has no formatter of its own, so a bare
                        `!= null` here let '' and whitespace-only strings through to
                        Number('').toLocaleString(), which is "0", rendering "0 acres" for a value
                        that is not there, and let a malformed string like '1,200' through to render
                        the literal text "NaN acres" (Number('1,200') is NaN). The three-part check
                        below closes both: != null excludes the field's actual absent shape (stored
                        as null, not ''), trim()!=='' excludes blank strings, and
                        Number.isFinite(Number(...)) excludes anything that does not parse. Live
                        details.lotSize is null (5006 docs) or a double (36), never a string (26.08
                        measurement) — this state is unreachable today, guarded for the field's own
                        domain regardless. 26.08, second review pass: unit is lower-case "acres" per
                        the export (node property-dashboard/details/Frame 321), not "Acres". */}
                    <FieldValue value={
                      details.lotSize != null && String(details.lotSize).trim() !== '' && Number.isFinite(Number(details.lotSize))
                        ? `${Number(details.lotSize).toLocaleString()} acres`
                        : null
                    } />
                  </div>
                  <div>
                    <span className={labelCls}>Flood Zone</span>
                    <div className="flex items-center gap-x-[6px]">
                      <FieldValue value={details.floodZone} />
                      {details.floodZone && details.floodZone !== 'X' && details.floodZone.toLowerCase() !== 'none'}
                    </div>
                  </div>
                  <div>
                    <span className={labelCls}>Parking</span>
                    <FieldValue value={details.parking} />
                  </div>
                </div>

                {/* HOA — gated like Schools below, not always-on: 26.08 measurement, a fee has a
                    value on 1 of 5042 live objects and amenities on 5 — the two counts are not
                    stated as disjoint, so "neither" is not quoted as an exact figure, only that
                    almost none of the total carry either. An ungated section drew two
                    populated-looking labels reading "—" on almost every property — a positive claim
                    of "no data" on a section that should not exist at all for that property. */}
                {hasHoa && (
                  // -mt-[15px]: this rare state (mr-06: 1 of 5042 for the fee, 5 for amenities) still
                  // keeps a short-text card at ~660px under min-height, rather than the few extra
                  // pixels min-height alone would grow it by; scoped to a block that never renders in
                  // the common case, so that layout is untouched. README.md has the rest.
                  <div className="-mt-[15px]">
                    <p className={labelSectionCls}>HOA</p>
                    <div className="grid grid-cols-3 gap-x-[24px]">
                      <div>
                        <span className={labelCls}>HOA Fee</span>
                        {/* `> 0`, not `!= null` alone, unlike Lot Size/Total Tax below which render
                            a stored 0 as data. Left as-is: hoaFeeAmt is null when no
                            "Association Fee:" entry exists, and NaN when one exists but does not
                            parse (e.g. "Association Fee: $250" — Number("$250") is NaN, not null) —
                            `> 0` already excludes both the same way a `!= null` guard would need a
                            second clause for, so tightening this to render a stored 0 would be new
                            behaviour on a path this pass did not touch, not a fix to what `> 0`
                            already gets right. No live document holds 0 in this field today (26.08
                            measurement). */}
                        <FieldValue value={hoaFeeAmt != null && hoaFeeAmt > 0
                          ? `${formatPrice(hoaFeeAmt)}${hoaFreq ? `/${hoaFreq}` : ''}`
                          : null} />
                      </div>
                      <div className="col-span-2">
                        <span className={labelCls}>Amenities</span>
                        <FieldValue value={hoaAmenities} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Schools */}
                {(schools.elementary || schools.middle || schools.high) && (
                  <div>
                    <p className={labelSectionCls}>Schools</p>
                    <div className="grid grid-cols-3 gap-x-[16px]">
                      {[
                        { level: 'Elementary School', name: schools.elementary, rate: schools.elementary_state_rate },
                        { level: 'Middle School', name: schools.middle, rate: schools.middle_state_rate },
                        { level: 'High School', name: schools.high, rate: schools.high_state_rate },
                      ].map(({ level, name, rate }) => {
                        // 26.08 mail: a school with no rating still shows the badge — grey 0/10, not
                        // an absent badge. getSchoolRatingColor (above, top of file) already maps a
                        // falsy rate to #C4CCBE, so defaulting here rather than inside that function
                        // keeps the colour scale itself untouched and also fixes the badge TEXT: `{rate}` on a
                        // null/undefined rate renders nothing, but `parseInt(null, 10)` is `NaN`,
                        // which *does* render as the literal text "NaN" — so the default has to
                        // happen before display, not be read off the colour function's own parsing.
                        //
                        // That rule is scoped to a school that exists. 26.08 DB measurement (7 live
                        // documents, ids in files/sow2/mr-06-property-dashboard.md): the missing level
                        // is stored as `high: "", high_state_rate: null` — an empty string, not an
                        // absent key — so the gate below has to fail on `''` as well as null/undefined,
                        // which a plain `name &&` already does (both are falsy). Rendering a badge
                        // there would claim a school scored zero where there is no school. The mail's
                        // own case — a school that IS named with no rating — has zero matching
                        // documents on the live DB right now; the `|| 0` default below still exists for
                        // that case because a rule scoped to a live count would silently stop applying
                        // the day a real one shows up. `||`, not `??`: a stored rate can in principle
                        // be `''` or `NaN` as well as null/undefined, and `??` only catches the latter
                        // two — `'' ?? 0` is `''` (renders the badge with a blank leading number,
                        // "/10"), and `NaN ?? 0` is `NaN` (renders the literal text "NaN/10"). `||`
                        // treats every one of those as falsy and maps all of them to the same 0.
                        const displayRate = rate || 0;
                        return (
                          <div key={level}>
                            <span className={labelCls}>{level}</span>
                            <div className="flex items-center gap-x-[8px] mt-[6px]">
                              {name && (
                                // 26.08 second review pass, measured against
                                // property-dashboard/details/Frame 327/Frame 595/Frame 607 (three
                                // badges, all identical): circle 40x40 (rx=20), rate 16px bold,
                                // "/10" suffix 11px regular. Colours and the 8px gap to the school
                                // name were already correct and are untouched.
                                <span
                                  className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-bold shrink-0"
                                  style={{ backgroundColor: getSchoolRatingColor(displayRate) }}
                                >
                                  <span style={{ fontSize: 16 }}>{displayRate}</span>
                                  <span style={{ fontSize: 11, fontWeight: 400 }}>/10</span>
                                </span>
                              )}
                              <FieldValue value={name} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Taxes */}
                <div>
                  <p className={labelSectionCls}>Taxes</p>
                  <div className="grid grid-cols-3 gap-x-[24px]">
                    <div>
                      <span className={labelCls}>Tax Year</span>
                      <FieldValue value={details.tax?.year} />
                    </div>
                    <div>
                      <span className={labelCls}>Total Tax</span>
                      {/* != null, not truthy: formatPrice(0) is deliberately "$0" (see formatPrice
                          above, top of file), so a truthy gate here would throw away that zero and
                          show the em dash instead — same reasoning as Lot Size above, not the same
                          guard: formatPrice already turns '' into '' on its own, so this field does
                          not need Lot Size's extra Number.isFinite/trim guard. */}
                      <FieldValue value={details.tax?.amount != null ? formatPrice(details.tax.amount) : null} />
                    </div>
                    <div />
                  </div>
                </div>
              </div>
            )}

            {/* History tab */}
            {historyTab && (
              <div>
                {/* Table header — sticky, not in scroll area */}
                <div className="flex gap-[16px] py-[4px] bg-white rounded-t-[4px]">
                  <div className="w-[224px] flex items-center pl-[12px]">
                    <span className="text-[14px] font-bold text-brand-dark">Date</span>
                  </div>
                  <div className="w-[224px] flex items-center">
                    <span className="text-[14px] font-bold text-brand-dark"> Status</span>
                  </div>
                  <div className="w-[127px] flex items-center pr-[12px]">
                    <span className="text-[14px] font-bold text-brand-dark">Price</span>
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="history-scroll">
                  {sortedYears.length === 0 && (
                    <p className="text-[13px] text-brand-unselectedGray py-[20px] pl-[12px]">No history available.</p>
                  )}
                  {sortedYears.map(year => (
                    <div key={year}>
                      {groupedHistory[year].map((entry, i) => {
                        const Icon = STATUS_ICON_MAP[entry.event_name]
                        const badgeBg = STATUS_BADGE_BG[entry.event_name]
                        return (
                          <div
                            key={i}
                            className="flex gap-[16px] items-center h-[52px] border-b border-brand-rowBorder"
                          >
                            <div className="w-[224px] pl-[12px] py-[2px] shrink-0">
                              <span className="text-[14px] text-brand-dark">{formatHistoryDate(entry.date)}</span>
                            </div>
                            <div className="w-[224px] flex items-center gap-[8px] shrink-0">
                              {Icon && (
                                badgeBg ? (
                                  <div className="size-[20px] rounded-full flex items-center justify-center shrink-0" style={{ background: badgeBg }}>
                                    <Icon />
                                  </div>
                                ) : (
                                  <Icon className="size-[20px] shrink-0" />
                                )
                              )}
                              <span className="text-[14px] text-brand-dark">
                                {STATUS_LABEL_MAP[entry.event_name] ?? entry.event_name ?? ''}
                              </span>
                            </div>
                            <div className="w-[127px] pr-[12px] py-[2px] shrink-0">
                              <span className="text-[14px] text-brand-dark">
                                {entry.price ? formatPrice(entry.price) : ''}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col" style={{ flex: '1.2', minWidth: 0 }}>

            {/* Notes + Major Systems card, matched to Property Details at 660px (Comment Set #2,
                item 2) — overflowed by 39px against PD9's pinned 485px textarea and 24px gap;
                closed via spacing PD9 does not pin. Textarea height itself moved again since, see
                the comment on it below. min-height, not height: two more states still overflow a
                fixed 660 (narrow Major Systems wrap, long HOA text). Arithmetic and both states,
                and why 375 keeps its own gap: README.md, "min-height, not height". */}
            <div className="bg-white border border-brand-fieldBdr rounded-[8px] min-h-[660px] px-[24px] pt-[24px] pb-[2px]">
              {/* PD 8: 16/800, quick-actions/Frame 310_2. */}
              <p className="text-[16px] font-extrabold text-brand-dark mb-[4px]">Notes</p>
              {/* PD 9's 485 pinned "Last Modified" 24px above Major Systems via the mt-[24px] below,
                  a gap this value does not touch. Comment Set #3 item 1 asks a different gap: Major
                  Systems sat only 5px above the card's own bottom border at 1280 (measured, not the
                  660 the card holds to). 466 closes it to the requested 24 there; the mt-[24px] gap
                  is unchanged. Arithmetic and the 375 exception: README.md, "min-height, not height". */}
              <textarea
                className="block w-full h-[466px] text-[14px] border border-brand-fieldBdr rounded-[4px] px-[12px] py-[8px] bg-white outline-none hover:border-brand-green focus:border-brand-green resize-none placeholder:text-brand-placeholder"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                onBlur={() => {
                  // setNotesModified only on save's own success -- this line running unconditionally,
                  // regardless of whether the PATCH actually succeeded, was the whole defect: a failed
                  // save still advanced "Last Modified" and read as a successful one.
                  save({ 'notes.text': notes }).then((ok) => {
                    if (ok) setNotesModified(new Date().toISOString());
                  });
                }}
                placeholder="Add notes…"
              />
              <span className="text-[11px] text-brand-secondary mt-[2px] block h-[16px]">
                {
                  notesModified && `Last Modified: ${formatDate(notesModified)}`
                }
              </span>

              {/* Major Systems — 24 below Last Modified, see PD 9 above. */}
              <div className="mt-[24px]">
                <p className={labelSectionCls}>Major Systems</p>
                {/* flex-wrap, not a grid: the fields are a fixed 67 (M4), so a wrapped group keeps its
                    width and never stretches to fill the line. */}
                <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[12px] mt-[8px]">
                  {[
                    { label: 'AC', name: 'AC', value: acYear, onChange: setAcYear, field: 'details.ac' },
                    { label: 'Furnace', name: 'Furnace', value: furnaceYear, onChange: setFurnaceYear, field: 'details.furnace' },
                    { label: 'Roof', name: 'Roof', value: roofYear, onChange: setRoofYear, field: 'details.roof' },
                    { label: 'WH', name: 'Water Heater', tooltip: 'Water Heater', value: whYear, onChange: setWhYear, field: 'details.water_heater' },
                  ].map(({ label, name, tooltip, value, onChange, field }) => (
                    <div key={field} className="flex shrink-0 items-center gap-x-[8px]">
                      <MajorSystemLabel
                        htmlFor={`major-system-${field}`}
                        tooltip={tooltip}
                        tooltipId={`major-system-${field}-tooltip`}
                        open={openTooltip === field}
                        setOpen={isOpen => setOpenTooltip(isOpen ? field : null)}
                      >
                        {label}
                      </MajorSystemLabel>
                      <input
                        id={`major-system-${field}`}
                        type="text"
                        aria-label={name}
                        aria-describedby={tooltip && openTooltip === field ? `major-system-${field}-tooltip` : undefined}
                        onFocus={tooltip ? () => setOpenTooltip(field) : undefined}
                        placeholder="YYYY"
                        value={value}
                        // Digits only -- a year has no legal '.', '-' or 'e', and this also blocks
                        // paste/drop, unlike fieldLimits.js's type="number" fields (separate handlers
                        // each).
                        onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
                        onBlur={() => {
                          // Real bound, not Number.isFinite -- a huge-but-finite digit string (20
                          // nines) sailed through that check unrejected. Clears the field on reject
                          // too, so the display never shows a value the wire just nulled.
                          const n = Number(value)
                          const inRange = n >= YEAR_FIELD_MIN && n <= YEAR_FIELD_MAX
                          const valid = value !== '' && inRange
                          if (!valid) onChange('')
                          save({ [field]: valid ? n : null })
                          if (tooltip) setOpenTooltip(null)
                        }}
                        className="w-[67px] h-[40px] text-[14px] font-medium border border-brand-fieldBdr rounded-[4px] px-[8px] py-[6px] bg-white outline-none placeholder:text-brand-placeholder focus:border-brand-green hover:border-brand-green text-center"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default PropertyDashboard;
